import { useEffect, useMemo, useState } from "react";

const STR = {
  es: {
    title: "Cancelar suscripción",
    intro: "Confirma tu baja del mailing.",
    email: "Email",
    reason: "Motivo (opcional)",
    reason_ph: "Ej. demasiados correos, contenido no relevante, etc.",
    btn: "Cancelar suscripción",
    btn_loading: "Procesando...",
    missing_email: "Falta el email.",
    processing: "Procesando...",
    err_generic: "No se pudo procesar la baja.",
    err_network: "Error de red. Intenta nuevamente.",
    lang_label: "Idioma",
    lang_es: "ES",
    lang_en: "EN",
    sr_lang: "Seleccione idioma",
  },
  en: {
    title: "Unsubscribe",
    intro: "Confirm you want to unsubscribe.",
    email: "Email",
    reason: "Reason (optional)",
    reason_ph: "E.g., too many emails, not relevant content, etc.",
    btn: "Unsubscribe",
    btn_loading: "Processing...",
    missing_email: "Email is required.",
    processing: "Processing...",
    err_generic: "We couldn't process your request.",
    err_network: "Network error. Please try again.",
    lang_label: "Language",
    lang_es: "ES",
    lang_en: "EN",
    sr_lang: "Select language",
  },
};

function encode(data) {
  // Convierte objeto a x-www-form-urlencoded
  return new URLSearchParams(data).toString();
}

export default function Unsubscribe() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [lang, setLang] = useState("es");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qLang = params.get("lang");
    const qEmail = params.get("email");
    if (qLang === "en") setLang("en");
    if (qEmail) setEmail(qEmail || "");
  }, []);

  const t = useMemo(() => STR[lang], [lang]);

  const switchLang = (newLang) => {
    if (newLang !== "es" && newLang !== "en") return;
    setLang(newLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", newLang);
    window.history.replaceState({}, "", url.toString());
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setMsg(t.missing_email);
      return;
    }

    setMsg(t.processing);
    setLoading(true);

    // Construimos los campos para Netlify Forms
    const payload = {
      "form-name": "unsubscribe",       // 👈 debe coincidir con el <form name="unsubscribe">
      timestamp: new Date().toISOString(),
      email,
      reason,
      source_page: window.location.href,
      user_agent: navigator.userAgent,
      ip: "", // si luego quieres capturar IP real, lo hacemos con una Function
      "bot-field": "", // honeypot vacío
    };

    try {
      // En Netlify Forms, se POSTea a "/" con x-www-form-urlencoded
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        setMsg(`${t.err_generic} Detalle: ${text}`);
      } else {
        // Netlify guardará la sumisión en el dashboard
        window.location.href = `/thank-you-unsubscribe.html?lang=${lang}`;
      }
    } catch (err) {
      setMsg(t.err_network);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-xl mx-auto p-4" style={{ color: "#0f172a" }}>
      {/* Encabezado + switch de idioma */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold">{t.title}</h1>

        {/* Language switch (accesible) */}
        <div className="langswitch text-sm" aria-label="Language selector">
          <span className="sr-only">
            {t.sr_lang} / {t.sr_lang}:
          </span>
          <a
            href="#"
            id="to-es"
            onClick={(e) => {
              e.preventDefault();
              switchLang("es");
            }}
            className={lang === "es" ? "font-semibold underline" : ""}
          >
            ES
          </a>{" "}
          |{" "}
          <a
            href="#"
            id="to-en"
            onClick={(e) => {
              e.preventDefault();
              switchLang("en");
            }}
            className={lang === "en" ? "font-semibold underline" : ""}
          >
            EN
          </a>
        </div>
      </div>

      <p className="mb-4">{t.intro}</p>

      <form onSubmit={onSubmit} className="space-y-4" name="unsubscribe">
        {/* Campo honeypot (oculto para humanos, visible para bots) */}
        <input type="text" name="bot-field" className="hidden" tabIndex={-1} autoComplete="off" />

        <div>
          <label className="block mb-1 font-medium">{t.email}</label>
          <input
            type="email"
            value={email}
            readOnly
            className="w-full border border-gray-400 rounded p-2 bg-gray-50"
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">{t.reason}</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t.reason_ph}
            className="w-full border border-gray-400 rounded p-2"
            rows={3}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-[#0d3558] text-white rounded"
        >
          {loading ? t.btn_loading : t.btn}
        </button>

        {msg && <div className="mt-3 text-sm text-red-600 whitespace-pre-wrap">{msg}</div>}
      </form>
    </section>
  );
}
